import React, { FC, useCallback, useMemo } from "react";
import { Station, StationRawData } from "@shared/models";
import { SensorDataSessionChart } from "@containers/SensorDataSession/components";
import { MONGO_INTERVAL_OPTIONS } from "@shared/constants";
import { Option, StationViewRawDataFilter } from "@shared/interfaces";
import { addSecondsToDate, convertDateToTimezone, getCurrentTimezone } from "@shared/utils";

import { combineStationValues } from "./chartHelpers";

interface StationViewRawDataChartProps {
  rows: StationRawData[];
  filter: StationViewRawDataFilter | null;
  station: Station;
  isLoading: boolean;
  startDate: Date;
  endDate: Date;
  onChangeFilter: (updatedFilter: Partial<StationViewRawDataFilter>) => void;
}

export const AUTOPLAY_INTERVAL = 5000;

const StationViewRawDataChart: FC<StationViewRawDataChartProps> = ({
  station,
  startDate,
  endDate,
  filter,
  rows,
  isLoading,
  onChangeFilter,
}) => {
  const chartDomain = useMemo(() => {
    if (!filter?.start || !filter.end || rows.length) return undefined;

    const currentUserTimezone = getCurrentTimezone();

    return [
      convertDateToTimezone(filter.start, station.timezone || currentUserTimezone)?.toLocaleTimeString() as string,
      convertDateToTimezone(filter.end, station.timezone || currentUserTimezone)?.toLocaleTimeString() as string,
    ];
  }, [filter?.start, filter?.end, rows.length, station]);

  const chartValues = useMemo(
    () => [
      {
        rows,
        station_id: station.id,
      },
    ],
    [rows, station.id],
  );

  const combinedValues = useCallback(
    (values: { rows: StationRawData[]; station_id: number }[]) => {
      return combineStationValues(station)(values);
    },
    [station],
  );

  const handleChangeInterval = useCallback(
    (interval: Option<number>) => {
      if (filter) {
        onChangeFilter({
          interval,
          start: filter.start,
          end: addSecondsToDate(new Date(filter.start), interval.value).toISOString(),
        });
      }
    },
    [filter, onChangeFilter],
  );

  return (
    <SensorDataSessionChart
      loading={isLoading}
      domain={chartDomain}
      elements={[station]}
      filter={filter}
      minTime={startDate}
      maxTime={endDate}
      values={chartValues}
      onChangeFilter={onChangeFilter}
      combineValues={combinedValues}
      intervalOptions={MONGO_INTERVAL_OPTIONS}
      isChartWithConfigVisible={false}
      handleIntervalChange={handleChangeInterval}
      isPrevNextButtonsVisible={true}
      autoPlayInterval={AUTOPLAY_INTERVAL}
    />
  );
};

export default StationViewRawDataChart;
